import { Component, OnInit, ViewChild, inject } from "@angular/core";
import { TpaConnectionDataService } from "@app/external-connections/services/tpa-connection.data.service";
import { GenericListConfig } from "@app/shared/models/GenericList/GenericList";
import { GenericListFieldType } from "@app/shared/models/GenericList/GenericListField";
import { CerrixPromptService } from "@app/shared/services/cerrix-prompt.service";
import { nameof } from "@methods/jeffs-toolkit";
import { TabModel } from "@models/generic/TabModels/TabModel";
import { ToastrService } from "ngx-toastr";
import { Observable, firstValueFrom } from "rxjs";
import { TpaConnectionWizardComponent } from "../connection-wizard/connection-wizard.component";
import { TpaConnectionModel } from "../models/tpaConnectionModel";
import { injectTranslation, provideTranslationScope } from "@app/shared/i18n/translation-scope";
import { scope } from "@app/external-connections/i18n/scope";
import { GenericListManagerComponent } from "@app/shared/generic-managers/generic-list-manager/generic-list-manager.component";

@Component({
    selector: "app-tpa-overview",
    templateUrl: "./tpa-overview.component.html",
    styleUrls: ["./tpa-overview.component.scss"],
    imports: [GenericListManagerComponent, TpaConnectionWizardComponent],
    viewProviders: [provideTranslationScope(scope)],
})
export class TpaConnectionOverviewComponent implements OnInit {
    private _ds = inject(TpaConnectionDataService);
    private _promptService = inject(CerrixPromptService);
    private _toastr = inject(ToastrService);
    private _translation = injectTranslation(scope);

    @ViewChild("connectionWizard") connectionWizard: TpaConnectionWizardComponent;
    tabID: string;
    tab: TabModel;

    config: GenericListConfig;

    async ngOnInit(): Promise<void> {
        await this._translation.ready();
        this.loadConfig();
    }

    loadConfig(): void {
        const t = (key: string, params?: Record<string, unknown>) =>
            this._translation.t(`connections.${key}`, params);
        this.config = <GenericListConfig>{
            name: t("thirdPartyAssuranceConnections"),
            dataMethod: (): Observable<TpaConnectionModel[]> => {
                return this._ds.getTpaConnections();
            },
            allowAdd: true,
            uiidAdd: "tpa-connection-add",
            allowDelete: true,
            idProp: nameof<TpaConnectionModel>((x: TpaConnectionModel): string => x.connectionGuid),
            isSortableByColumn: true,
            fields: [
                {
                    fieldName: nameof<TpaConnectionModel>((x: TpaConnectionModel) => x.id),
                    fieldType: GenericListFieldType.Text,
                    isReadonly: true,
                    hideInEdit: true,
                    hideInOverview: true,
                },
                {
                    fieldName: nameof<TpaConnectionModel>(
                        (x: TpaConnectionModel): string => x.connectionGuid
                    ),
                    fieldType: GenericListFieldType.Text,
                    isReadonly: true,
                    hideInEdit: true,
                    hideInOverview: true,
                },
                {
                    fieldName: nameof<TpaConnectionModel>(
                        (x: TpaConnectionModel): string => x.name
                    ),
                    prettyName: t("connectionNameField"),
                    fieldType: GenericListFieldType.Text,
                    isReadonly: true,
                },
                {
                    fieldName: nameof<TpaConnectionModel>(
                        (x: TpaConnectionModel) => x.apikeyExpiryDate
                    ),
                    prettyName: t("expiryDateApiKeyField"),
                    fieldType: GenericListFieldType.Date,
                    isReadonly: true,
                },
                {
                    fieldName: nameof<TpaConnectionModel>(
                        (x: TpaConnectionModel): string => x.description
                    ),
                    prettyName: t("descriptionField"),
                    fieldType: GenericListFieldType.TextArea,
                    isReadonly: true,
                },
                {
                    fieldName: nameof<TpaConnectionModel>(
                        (x: TpaConnectionModel): string => x.testType
                    ),
                    prettyName: t("testTypeField"),
                    fieldType: GenericListFieldType.Text,
                    isReadonly: true,
                },
                {
                    fieldName: nameof<TpaConnectionModel>((x: TpaConnectionModel) => x.startDate),
                    prettyName: t("startdateField"),
                    fieldType: GenericListFieldType.Date,
                    isReadonly: true,
                },
                {
                    fieldName: nameof<TpaConnectionModel>((x: TpaConnectionModel) => x.endDate),
                    prettyName: t("enddateField"),
                    fieldType: GenericListFieldType.Date,
                    isReadonly: true,
                },
            ],
            addOverride: async (): Promise<any> => {
                this.connectionWizard.connectionGuid = null;
                await this.connectionWizard.load(true);
            },
            editOverride: async (row: TpaConnectionModel): Promise<any> => {
                this.connectionWizard.connectionGuid = row.connectionGuid;
                await this.connectionWizard.load(true);
            },
            delete: async (row: TpaConnectionModel): Promise<boolean> => {
                const confirmed = await this._promptService
                    .confirm(
                        t("deleteConfirmTitle", { name: row.name }),
                        t("deleteConfirmBody")
                    )
                    .toPromise();

                if (!confirmed) return false;

                await firstValueFrom(this._ds.deleteTpaConnection(row.connectionGuid));
                this._toastr.success(
                    t("deleteSuccessMessage", { name: row.name }),
                    t("deleteSuccessTitle")
                );
                return true;
            },
        };
    }

    reload(): void {
        this.config.reload();
    }
}

